import { CrmPage } from './pages/crm/CrmPage';
import { hasCrmAccess, isCrmHost } from './pages/crm/crm-access';
import { Suspense, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { AuthLayout } from '@gokrew/ui/layouts';
import NotFoundPage from '@gokrew/ui/pages/NotFound';
import NoAccessPage from '@gokrew/ui/pages/NoAccessPage';
import { AppLoaderPage } from '@gokrew/ui/components/AppLoaderPage';
import { AppRouter } from '@gokrew/ui/components/AppRouter';
import { lazyWithPreload } from '@gokrew/ui/utility/lazyWithPreload';
import { createRoutePreloader } from '@gokrew/ui/utility/createRoutePreloader';
import { useAuth } from '@/context/AuthContext';

// ─── Priority: bundled in main chunk ─────────────────────────────────────────
import { LoginPage } from './pages/auth/Login';
import { ForgotPasswordPage } from './pages/auth/ForgotPassword';
import { ResetPasswordPage } from './pages/auth/ResetPassword';

// The Super-Admin-only Global Settings page calls the platform-settings API,
// which the backend guards with SuperAdminGuard — a regular admin who
// reaches /settings would get a live-looking toggle that 403s on load and
// on save. Gate the route to match: wait for the profile fetch (isLoading)
// before deciding, so a super admin doesn't briefly flash a no-access page
// while their role is still resolving.
function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user?.role !== 'super_admin') return <NoAccessPage />;
  return children;
}

// The dashboard shows revenue and cross-customer data, and its API is
// super-admin only. `/` is also the default landing, so a regular admin is
// sent to the companies list rather than a no-access page. Waits for the
// profile so a super admin is not bounced while their role resolves.
function DashboardRoute() {
  const { user, isLoading } = useAuth();
  if (isLoading || !user) return <AppLoaderPage />;
  if (isCrmHost) return <Navigate to="/crm" replace />;
  if (user.role !== 'super_admin') {
    return <Navigate to="/companies" replace />;
  }
  return <DashboardPage.component />;
}

// ─── lazyWithPreload: lazy + preload in one declaration ──────────────────────
import { MainLayout } from './layout/MainLayout';
const DashboardPage = lazyWithPreload(
  () => import('./pages/dashboard/DashboardPage'),
  'DashboardPage'
);
// The prospect's live AI demo (no sign-in), reached from a CRM demo link.
const LiveDemoPage = lazyWithPreload(
  () => import('./pages/live-demo/LiveDemoPage'),
  'LiveDemoPage'
);
const SettingsPage = lazyWithPreload(
  () => import('./pages/settings/SettingsPage'),
  'SettingsPage'
);
const AdminPage = lazyWithPreload(
  () => import('./pages/admin-users/AdminPage'),
  'AdminPage'
);
const CompaniesPage = lazyWithPreload(
  () => import('./pages/companies/CompaniesPage'),
  'CompaniesPage'
);
const CompanyDetailsPage = lazyWithPreload(
  () => import('./pages/companies/CompanyDetailsPage'),
  'CompanyDetailsPage'
);
const ProfilePage = lazyWithPreload(
  () => import('./pages/profile/ProfilePage')
);
const UserDetailsPage = lazyWithPreload(
  () => import('./pages/user-details/Userdetailspage'),
  'UserDetailsPage'
);
const UserExplorerPage = lazyWithPreload(
  () => import('./pages/user-details/Userexplorerpage'),
  'UserExplorerPage'
);
const SystemSettingsPage = lazyWithPreload(
  () => import('./pages/system-settings/SystemSettingsPage')
);
const ProductMetricsPage = lazyWithPreload(
  () => import('./pages/product-metrics/ProductMetricsPage'),
  'ProductMetricsPage'
);
const ScanReportsPage = lazyWithPreload(
  () => import('./pages/scan-reports/ScanReportsPage'),
  'ScanReportsPage'
);

// ─── Preloadable routes (fed into createRoutePreloader) ─────────────────────
const preloadableRoutes = [
  { preload: DashboardPage.preload },
  { preload: SettingsPage.preload },
  { preload: AdminPage.preload },
  { preload: CompaniesPage.preload },
  { preload: CompanyDetailsPage.preload },
  { preload: ProfilePage.preload },
  { preload: UserExplorerPage.preload },
  { preload: UserDetailsPage.preload },
  { preload: SystemSettingsPage.preload },
  { preload: ScanReportsPage.preload },
  { preload: ProductMetricsPage.preload },
];

const preloadRoutes = createRoutePreloader(preloadableRoutes);

export function AppRoutes() {
  const { isInitializing, isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (isAuthenticated && !isCrmHost) {
      preloadRoutes();
    }
  }, [isAuthenticated]);

  if (isInitializing) return null;

  return (
    <AppRouter>
      <Suspense fallback={<AppLoaderPage />}>
        <Routes>
          {/* Auth routes */}
          <Route
            element={
              <AuthLayout>
                <Outlet />
              </AuthLayout>
            }
          >
            <Route path="/login" element={<LoginPage />} />
          </Route>

          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/live/:token" element={<LiveDemoPage.component />} />

          {isCrmHost ? (
            <Route element={<MainLayout />}>
              <Route path="/crm/:section?" element={<CrmPage />} />
              <Route path="*" element={<Navigate to="/crm" replace />} />
            </Route>
          ) : (
            <>
              <Route element={<MainLayout />}>
                <Route
                  path="/crm/:section?"
                  element={
                    hasCrmAccess(user?.email) ? <CrmPage /> : <NoAccessPage />
                  }
                />
              </Route>
              {/* Admin app routes */}
              <Route element={<MainLayout />}>
                <Route index element={<DashboardRoute />} />
                <Route
                  path="/admins"
                  element={<Navigate to="/settings/admins" replace />}
                />
                <Route
                  path="/companies"
                  element={<CompaniesPage.component />}
                />
                <Route
                  path="/companies/:id"
                  element={<CompanyDetailsPage.component />}
                />
                <Route
                  path="/personal-information"
                  element={<ProfilePage.component />}
                />
                <Route path="/users" element={<UserExplorerPage.component />} />
                <Route
                  path="/users/:userId"
                  element={<UserDetailsPage.component />}
                />
                <Route
                  path="/product-metrics"
                  element={
                    <RequireSuperAdmin>
                      <ProductMetricsPage.component />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="/scan-reports"
                  element={
                    <RequireSuperAdmin>
                      <ScanReportsPage.component />
                    </RequireSuperAdmin>
                  }
                />
                <Route path="/settings" element={<SettingsPage.component />}>
                  <Route index element={<Navigate to="admins" replace />} />
                  <Route path="admins" element={<AdminPage.component />} />
                  <Route
                    path="platform"
                    element={
                      <RequireSuperAdmin>
                        <SystemSettingsPage.component />
                      </RequireSuperAdmin>
                    }
                  />
                </Route>
              </Route>
            </>
          )}
          {/* Fallback */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppRouter>
  );
}
